import { arrivalClock } from '../components/ArrivalInput';
import { useNoticeToast } from '../lib/toast';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, type ListResult } from '../lib/api';
import {
  DataTable,
  ErrorBox,
  Pager, usePageSize,
  dash,
  money,
  useList,
  wallClock,
  type Column,
  type Row,
} from '../components/DataTable';
import DisptDetail from './DisptDetail';
import RecvForm from './RecvForm';
import RecvIntakeForm from './RecvIntakeForm';
import DisptForm from './DisptForm';
import RecvCopy from './RecvCopy';
import RecvPane from './RecvPane';
import { clearClip, hasTextSelection, isTypingTarget, readClip, writeClip, type Clip } from '../lib/clip';
import RecvList from './RecvList';
import Modal from '../components/Modal';
import { canScreen, useMe } from '../lib/me';
import { useAutoSearch } from '../lib/search';
import { useUrlSearch } from '../lib/deepLink';
import { usePageActive } from '../lib/pageActive';
import { useSort } from '../lib/sort';
import { peekSticky, useSticky } from '../lib/sticky';
import { DISPATCH_PATH, dispatchKey, usePageTabs } from '../lib/pageTabs';
import { usePageRefresh, useRefreshRequest } from '../lib/pageRefresh';
import { DISPT_STATUS_TONE, disptStatusLabel } from '../lib/disptStatus';

const asRows = <T,>(p: Promise<ListResult<T>>) => p as unknown as Promise<ListResult<Row>>;

/**
 * 일반배차 조회.
 *
 * 통근은 화면에 올라오지 않는다 — API 가 `v_*_general` 뷰만 읽고, 그 뷰가 `recv_type='일반'` 을 강제한다.
 * 여기서 다시 거르지 않는다(이중 방어처럼 보이지만 실제로는 "어디서 걸렀는지"를 흐린다).
 *
 * **용어**: `dispt` = **노선**(차 N대가 필요한 하나의 운행), `dispt_car_sch` = **배차**(그 노선에 차 1대 투입).
 * 화면 문구는 이 뜻으로 통일돼 있다 — DB·API 이름은 레거시 그대로 `dispt` 다.
 * ⚠️ 상태값(`배차완료`·`유찰` 등)은 **데이터**다(`v_dispt` 계산식). 문구 통일 대상이 아니다.
 */

/** 레거시 View_DisptStatus 가 내는 값. 실데이터에 존재하는 4종만 노출한다. */
const DISPT_STATUS = ['배차완료', '입찰', '유찰가능', '유찰', '비공개입찰', '취소'];
const RECV_STATUS = ['완료', '접수', '취소'];

/** 상태 색·글자(#207·#291·#323) — 노선 상세와 같이 쓴다(`lib/disptStatus.ts`). */
const statusTone = DISPT_STATUS_TONE;
const statusLabel = disptStatusLabel;

/**
 * 운행료 단가 칸의 합계 자리. **1대당 단가는 더하지 않는다** — 그 합은 아무 뜻이 없고,
 * 숫자가 있으면 누군가 총액으로 읽는다. 총액은 오른쪽 '운행료 합계' 칸이다.
 */
function UnitNoTotal() {
  return (
    <span className="font-normal text-ink-faint" title="1대당 단가라 합계를 내지 않습니다. 총액은 오른쪽 ‘합계’ 칸입니다.">
      —
    </span>
  );
}

/** 번호(NO) 칸 — 레거시와 같은 번호. 눈에 띌 필요는 없고 찾을 때 읽히면 된다. */
function NoCell({ value }: { value: unknown }) {
  return <span className="text-xs text-ink-soft">{dash(value)}</span>;
}

/** 청구요청 칸(#418) — 체크 모양만(읽기 전용). 마우스를 올리면 청구상태·요청일. */
function BillReqCell({ value }: { value: unknown }) {
  const b = value as { status?: string; at?: string | null } | null;
  if (!b?.status) return <span className="text-ink-soft">☐</span>;
  return (
    <span className="font-semibold text-emerald-700" title={`${b.status}${b.at ? ` · 청구요청일 ${b.at}` : ''}`}>
      ☑
    </span>
  );
}

/**
 * 관리A 수당 칸(#475) — 노선에 관리기사A 가 있을 때만. 그 기사 모두 수당(영업정산 기사 지급)이 들어 있으면 ☑,
 * 아무도 없으면 ☐, 일부만이면 「1/2」. 마우스를 올리면 기사별 금액. 읽기 전용(수당·공제 등록에서 넣으면 저절로).
 */
function APayCell({ value }: { value: unknown }) {
  const list = Array.isArray(value) ? (value as { name?: string; amt?: string | null }[]) : [];
  if (list.length === 0) return <span className="text-ink-faint">—</span>;
  const paid = list.filter((x) => x.amt != null && Number(x.amt) > 0).length;
  const title = list
    .map((x) => `${dash(x.name)} ${x.amt != null && Number(x.amt) > 0 ? `수당 ${money(x.amt)}` : '수당 없음'}`)
    .join('\n');
  if (paid === list.length) return <span className="font-semibold text-emerald-700" title={title}>☑</span>;
  if (paid === 0) return <span className="text-ink-soft" title={title}>☐</span>;
  return <span className="font-semibold text-amber-700" title={title}>{paid}/{list.length}</span>;
}

/**
 * #391(10-23) 입찰 신청 취소 — 기사앱엔 취소 버튼이 없다. 기사가 접수 담당자에게 전화해 승인받으면 여기서 취소한다.
 * 서버 `withdrawBid` 담당자 경로(접수·배차 입력 권한) · 그 노선의 대기 신청을 내린다.
 */
function BidWithdrawButton({ r }: { r: Row }) {
  const { me } = useMe();
  const refresh = useRefreshRequest();
  const [busy, setBusy] = useState(false);
  if (!canScreen(me, 'dispatch')) return null;
  return (
    <button
      type="button"
      className="rounded px-1.5 py-0.5 text-xs text-red-600 ring-1 ring-red-200 hover:bg-red-50 disabled:opacity-50"
      disabled={busy}
      onClick={async (e) => {
        e.stopPropagation();
        if (!window.confirm(`${dash(r.driver_name)} 기사님의 신청을 취소할까요?\n노선 ${dash(r.dispt_id)} 「${dash(r.dispt_name)}」`)) return;
        setBusy(true);
        try {
          await api.withdrawBid({ disptId: Number(r.dispt_id) });
          refresh();
        } catch (err) {
          window.alert((err as Error).message);
        } finally {
          setBusy(false);
        }
      }}
    >
      신청 취소
    </button>
  );
}

function Badge({ value }: { value: unknown }) {
  const v = dash(value);
  if (v === '—') return <span className="text-ink-faint">—</span>;
  return (
    <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${statusTone[v] ?? 'bg-canvas text-ink-soft'}`}>
      {statusLabel(v)}
    </span>
  );
}

interface Tab {
  id: string;
  label: string;
  hint: string;
  statuses: string[];
  /** 표로 그리는 탭만. 접수 탭은 표가 아니라 `RecvList`(목록)라 없다. */
  columns?: Column[];
  /** 이 탭의 행을 클릭하면 상세를 연다. */
  clickable?: boolean;
  /** 입찰 탭 전용 토글. 라벨이 곧 의미다. */
  pendingLabel?: string;
  /** 노선 탭 전용 토글. **기본으로 켜진다** — 이 탭의 일이 "대수 채우기"라서다. */
  unfilledLabel?: string;
  load: (a: {
    q?: string;
    limit: number;
    offset: number;
    status?: string;
    pendingOnly?: boolean;
    recvType?: string;
    recvId?: number;
    unfilledOnly?: boolean;
    dateFrom?: string;
    dateTo?: string;
    sort?: string;
    dir?: 'asc' | 'desc';
  }) => Promise<ListResult<Row>>;
}

/**
 * 탭 순서 = 업무 순서다(접수 → 노선 → 입찰). **접수가 기본 화면**이다 —
 * 실제 진입은 "이 접수 어떻게 됐나"이고, 노선은 접수 줄에서 그 자리에 펼쳐진다.
 *
 * 그래도 노선 탭을 없애지 않았다. 접수 쪽에는 **상태 축이 없다** — 실측(일반):
 * 대수가 안 찬 노선 **943건**이 접수 **377개**에 흩어져 있어서 접수 탭에서 찾으려면
 * 377개를 하나씩 펼쳐야 한다. 검색축(노선명·출발지)과 정렬(출발 임박순)도 접수 탭에 없다.
 * 두 탭은 같은 자료의 복사본이 아니라 **다른 축**이다.
 */
const TABS: Tab[] = [
  {
    id: 'recv',
    label: '접수',
    hint: '거래처 · 거래처담당자 · 요청 · 배차소속 · 접수번호',
    statuses: RECV_STATUS,
    // 표가 없다 — 왼쪽 칸은 `RecvList`(2줄 목록)로 그린다. 좁은 칸에서 표는 긴 거래처명에
    // 끌려가 뒤쪽 칸을 잘라먹는다(2026-07-30 실측). 접수일·접수자·청구방법·입찰 조건은
    // 오른쪽 요약 카드에 있다.
    load: (a) => asRows(api.listRecvs(a)),
  },
  {
    id: 'dispt',
    label: '노선',
    hint: '거래처 · 노선명 · 출발/도착 · 차량번호 · 기사명 · 번호',
    // #360 윤일향 — 「대수 미충족만」 체크는 뺐다(출발일 기간으로 보고 상태는 「배차현황」 칸·상태 고르기로 본다).
    statuses: DISPT_STATUS,
    columns: [
      // NO = 노선(배차) 번호. 레거시 버스인3 DisptId 와 **같은 번호**다(이관 때 그대로 옮겼고 새 행도 그 뒤를 잇는다).
      // 담당자끼리 "몇 번 건" 으로 소통하므로 맨 앞에 둔다. 검색창에 숫자만 치면 이 번호로도 찾는다.
      // phone: 폰에선 노선명을 왼쪽에 고정하고 번호·편도왕복·차종·운행료 1대당 단가·정산월은 뺀다(합계 칸이 남는다 · 2026-09-30 way).
      // 10-15 way — 「NO」 한 칸을 노선번호·접수번호 두 칸으로 나눠 머리글에 무슨 번호인지 적는다.
      { key: 'dispt_id', label: '노선번호', align: 'right', phone: 'hide', render: (r) => <NoCell value={r.dispt_id} /> },
      { key: 'recv_id', label: '접수번호', align: 'right', phone: 'hide', render: (r) => <NoCell value={r.recv_id} /> },
      { key: 'dispt_status', label: '상태', render: (r) => <Badge value={r.dispt_status} /> },
      // #361 — 접수구분(일반) 칸 대신 배정 기사 이름(노선을 안 열어도 누가 가는지). 구분은 위 「구분 전체」 고르기로 거른다.
      { key: 'cust_name', label: '거래처' },
      { key: 'dispt_name', label: '노선명', phone: 'pin' },
      { key: 'driver_names', label: '기사명', noSort: true, render: (r) => dash(r.driver_names) },
      // #207 — 레거시 「일반배차관리」처럼 편도 출발·도착 / 왕복 출발·도착 시각을 나란히(왕복 칸은 편도면 비어 있다).
      { key: 'start_time', label: '편도 출발', render: (r) => wallClock(r.start_time) },
      { key: 'end_time', label: '편도 도착', phone: 'hide', render: (r) => arrivalClock(r.end_time) },
      { key: 'end_dept_time', label: '왕복 출발', phone: 'hide', render: (r) => arrivalClock(r.end_dept_time) },
      { key: 'start_arrv_time', label: '왕복 도착', phone: 'hide', render: (r) => arrivalClock(r.start_arrv_time) },
      { key: 'start_loc', label: '출발지' },
      { key: 'end_loc', label: '도착지' },
      { key: 'trip_type', label: '편도/왕복', phone: 'hide' },
      { key: 'car_kind', label: '차종', phone: 'hide' },
      // #372 — 연식제한 칸(#215)은 목록에서 뺐다(노선 상세 「입찰 공개·연식」에서 본다). 기사명은 노선명 바로 뒤.
      // #357 — 입찰시간(기사앱에 입찰로 연 시각 · 접수 칸) · 입찰마감. 비공개 노선은 '—'.
      { key: 'bidding_opendt', label: '입찰시간', phone: 'hide', noSort: true,
        render: (r) => (r.is_bidding === true ? wallClock(r.bidding_opendt) : '—') },
      { key: 'close_date', label: '입찰마감', phone: 'hide',
        render: (r) => (r.is_bidding === true ? wallClock(r.close_date) : '—') },
      // #291 — 「배정/필요」 한 칸을 레거시 말로 「댓수」(필요 대수)·「배차현황」(배정된 수) 두 칸으로.
      // 대수가 차면 '배차확정'이다 — 상태 배지의 근거를 같은 줄에서 볼 수 있게 나란히 둔다.
      // key `car_cnt` 는 그대로 둔다(계정에 저장된 칸 순서·숨김이 그 key 로 남아 있다).
      { key: 'car_cnt', label: '댓수', align: 'right', render: (r) => dash(r.car_cnt), totalRender: (v) => money(v.car_cnt) },
      {
        key: 'assigned_cnt',
        label: '배차현황',
        align: 'right',
        noSort: true, // 서버 정렬 목록(DISPT_SORT)에 없는 계산 값
        render: (r) => dash(r.assigned_cnt),
        totalRender: (v) => money(v.assigned_cnt),
      },
      // ⚠️ `cost_cust` 는 **노선 청구 총액**, `cost_driver` 는 **1대당 단가**다(CLAUDE.md 불변식 · issues 179).
      //    청구는 단가 칸이 따로 없다 — 노선 총액 그대로가 합계다(`cust_amt` = cost_cust, 청구 집계와 같은 값).
      //    운행료는 단가와 합계를 나란히 두고 합계는 서버가 곱해서 준다(`driver_amt` = 단가 × 대수).
      //    운행료 단가 칸의 **합계는 일부러 비운다** — 단가를 더한 값은 뜻이 없다(`UnitNoTotal`).
      // #291 — 머리글은 현업 말로: 청구액 → 거래처청구 · 운행료 단가 → 기사지급(값은 그대로 1대당 단가).
      { key: 'cust_amt', label: '거래처청구', align: 'right', render: (r) => money(r.cust_amt) },
      // #418 윤일향 — 그 접수·정산월에 청구 요청을 했는지(정산에 안 들어가도). 청구 요청하면 저절로 체크 · 취소 승인이면 풀린다.
      { key: 'bill_req', label: '청구요청', noSort: true, render: (r) => <BillReqCell value={r.bill_req} /> },
      // #475 윤일향 — 관리기사A 수당을 넣었는지(정산에서 일일이 안 열어 봐도). 관리기사A 없는 노선은 '—'.
      { key: 'a_pay', label: '관리A 수당', noSort: true, render: (r) => <APayCell value={r.a_pay} /> },
      {
        key: 'cost_driver',
        phone: 'hide',
        label: '기사지급',
        align: 'right',
        render: (r) => money(r.cost_driver),
        totalRender: () => <UnitNoTotal />,
      },
      { key: 'driver_amt', label: '운행료 합계', align: 'right', render: (r) => money(r.driver_amt) },
      { key: 'drive_mon', label: '정산월', phone: 'hide' },
    ],
    clickable: true,
    load: (a) => asRows(api.listDispts(a)),
  },
  {
    id: 'bid',
    label: '입찰',
    hint: '거래처 · 기사 · 차량번호 · 차주 · 소속 · 번호',
    statuses: [],
    // "미배정만" 을 기본값으로 두지 않는다 — 실데이터는 전 행이 소비 완료라 목록이 비어 보인다.
    pendingLabel: '미배정 신청만',
    columns: [
      // 신청 행 자체의 번호가 아니라 **신청한 노선의 번호**다 — 노선 탭 NO 와 같은 값이라 서로 찾아갈 수 있다.
      { key: 'dispt_id', label: '노선 NO', align: 'right', phone: 'hide', render: (r) => <NoCell value={r.dispt_id} /> },
      { key: 'applied_at', label: '신청', render: (r) => wallClock(r.applied_at) },
      { key: 'cust_name', label: '거래처' },
      { key: 'dispt_name', label: '노선명' },
      { key: 'start_time', label: '출발', render: (r) => wallClock(r.start_time) },
      { key: 'start_loc', label: '출발지', phone: 'hide' },
      { key: 'driver_name', label: '신청 기사', phone: 'pin' },
      { key: 'car_no', label: '차량' },
      { key: 'grp_name', label: '소속', phone: 'hide' },
      { key: 'bidding_type', label: '입찰유형', phone: 'hide' },
      { key: 'bidding_opendt', label: '입찰시간', phone: 'hide', render: (r) => wallClock(r.bidding_opendt) },
      { key: 'close_date', label: '입찰마감', render: (r) => wallClock(r.close_date) },
      {
        key: 'is_consumed',
        label: '처리',
        render: (r) =>
          r.is_consumed === true ? (
            <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-xs font-medium text-emerald-700">배차됨</span>
          ) : (
            <span className="inline-flex items-center gap-1.5">
              <span className="rounded bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-700">대기</span>
              <BidWithdrawButton r={r} />
            </span>
          ),
      },
    ],
    load: (a) => asRows(api.listBids(a)),
  },
];

/** 기간 조회 때 한 번에 받는 상한 — 서버 `paging` 상한과 같다. */
const PERIOD_ALL = 5000;

/** 이번 달(k=0)·다음 달(k=1)의 첫날·마지막 날(YYYY-MM-DD · 브라우저 시각 = 한국). */
function monthRange(k: number): [string, string] {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth() + k, 1);
  const last = new Date(now.getFullYear(), now.getMonth() + k + 1, 0);
  const f = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return [f(first), f(last)];
}

/** 화면 탭 이름(App.tsx `접수·배차 › 노선`) — 세부 탭마다 따로 화면 탭이다. */
export const DISPATCH_TABS: [string, string][] = TABS.map((t) => [t.id, t.label]);

export default function Dispatch() {
  /**
   * 세부 탭(접수·노선·입찰)마다 **따로 화면 탭**(2026-10-19 way 「노선 탭이 따로 안 떠 불편」 · 회계 #211 과 같은 방식).
   * 이 화면의 세부 탭은 자기 주소의 `?tab=` 으로 정해진다(화면 탭이 붙들고 있는 주소 — 새로고침해도 그대로).
   * 세부 탭 누르기 = 그 세부 탭의 화면 탭으로 — 이미 열려 있으면 그 화면(조건·입력 그대로), 없으면 새로.
   */
  const loc = useLocation();
  const navigate = useNavigate();
  const pageTabs = usePageTabs();
  const urlTab = new URLSearchParams(loc.search).get('tab');
  const tabId = TABS.some((t) => t.id === urlTab) ? urlTab! : TABS[0]!.id;
  const [term, setTerm] = useState('');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [pendingOnly, setPendingOnly] = useState(false);
  /** 노선 탭 기본값 **켬**. 끄면 배차완료까지 전량. */
  const [unfilledOnly, setUnfilledOnly] = useState(true);
  /** 노선 탭 출발일 기간(#197 — 레거시 「일반배차관리」 출발일 조회). 비면 기간 없음. */
  // 노선 탭은 열 때 이번 달 · 한 번 고쳤으면 그 기간(#322 — 아래 disptRange).
  const initRange = (): [string, string] =>
    tabId === 'dispt' ? ((peekSticky('dispatch.dispt.range') as [string, string] | undefined) ?? monthRange(0)) : ['', ''];
  const [dateFrom, setDateFrom] = useState(() => initRange()[0]);
  const [dateTo, setDateTo] = useState(() => initRange()[1]);
  /** 노선 탭 머리글 정렬(#207) — 서버가 정렬한다(쪽 단위로 받는다). 계정에 기억. */
  const [disptSort, toggleDisptSort] = useSort('dispatch.dispt');
  const [page, setPage] = useState(0);
  const [PAGE, setPAGE] = usePageSize('dispatch', 50);

  // 치는 대로 찾는다(입력이 멎으면 적용). 버튼·Enter 는 그대로 — `lib/search.ts` 참고.
  useAutoSearch(term, (v) => { setQ(v); setPage(0); });
  // 다른 화면에서 건너온 링크(?q=·?mon= — 정산현황 줄 보기 · lib/deepLink.ts)면 그 검색어로.
  useUrlSearch(setTerm, setQ);
  const [detail, setDetail] = useState<Row | null>(null);
  /** 노선 탭 한 번 누름 = 줄 표시만(#346 — 보려고 누를 때마다 상세 팝업이 덮었다). 두 번 누르면 상세. */
  const [picked, setPicked] = useState<number | null>(null);
  /** 신규 접수 — 접수 헤더와 노선 줄을 한 화면에서 받는다. */
  const [intake, setIntake] = useState(false);
  /** 기존 접수 수정 — 헤더만. 노선은 목록의 “노선 추가”에서 붙인다. */
  const [editRecv, setEditRecv] = useState<number | null>(null);
  const [disptForm, setDisptForm] = useState<{ recvId: number; disptId?: number; copyFromId?: number } | null>(null);
  /**
   * 복사 팝업. `preset` 이 있으면 `Ctrl+V` 로 열린 것이라 노선 선택·붙일 곳이 미리 채워진다.
   * ⚠️ 그래도 **자동 저장하지 않는다** — 붙여넣기는 행을 새로 만드는 일이고 배차는 건너뛸 수 있다.
   */
  const [copySrc, setCopySrc] = useState<
    { row: Row; preset?: { disptIds?: number[]; dest?: 'new' | 'same' | 'other'; otherRecvId?: number } } | null
  >(null);
  /** 오른쪽 칸에서 고른 노선 — `Ctrl+C` 가 "이 노선만" 담을 수 있게 위로 올려 둔다. */
  const [selDisptId, setSelDisptId] = useState<number | null>(null);
  const [clip, setClip] = useState<Clip | null>(() => readClip());
  /**
   * 접수 탭에서 고른 접수. **Row 가 아니라 id 로 들고 있다** —
   * 저장 후 목록을 다시 읽으면 Row 객체가 낡아서 화면이 옛 값을 보여준다.
   * 늘 새 `rows` 에서 찾아 쓰면 그 문제가 생기지 않는다.
   */
  const [selRecvId, setSelRecvId] = useState<number | null>(null);
  /** 노선 탭을 한 접수로 좁힌 상태. 인라인 200건 상한을 넘는 접수는 여기서 전체를 본다. */
  const [recvFilter, setRecvFilter] = useState<{ id: number; label: string } | null>(null);
  /**
   * 노선 탭에서 마지막으로 고른 출발일 기간(#322 윤일향 「10월~11월로 해 두고 접수 확인 뒤 돌아오면 10월로 바뀐다」).
   * 탭을 오가도 그대로 · 처음 열 때만 이번 달. 화면 메모리만(새로고침·로그아웃하면 이번 달로 — 날짜를 다음 날까지 들고 가지 않는다).
   * 「노선 전부 보기」(한 접수로 좁힘)의 빈 기간은 기억하지 않는다.
   */
  const [disptRange, setDisptRange] = useSticky<[string, string] | null>('dispatch.dispt.range', null);
  useEffect(() => {
    if (tabId === 'dispt' && !recvFilter) setDisptRange([dateFrom, dateTo]);
  }, [tabId, recvFilter, dateFrom, dateTo, setDisptRange]);
  const [recvType, setRecvType] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  useNoticeToast(notice, setNotice);
  const [nonce, setNonce] = useState(0);

  const { me } = useMe();
  const canWrite = canScreen(me, 'dispatch');

  const tab = useMemo(() => TABS.find((t) => t.id === tabId) ?? TABS[0]!, [tabId]);
  /** 노선 탭에서 출발일 기간을 걸면 그 기간 노선을 한 쪽에 전부 받는다(way 10-15 · 서버 상한 5000). */
  const allInPeriod = tabId === 'dispt' && !!(dateFrom || dateTo);

  const { rows: loadedRows, loading, error, total } = useList(
    () =>
      tab
        .load({
        q: q || undefined,
        limit: allInPeriod ? PERIOD_ALL : PAGE,
        offset: allInPeriod ? 0 : page * PAGE,
        status: status || undefined,
        pendingOnly: tab.pendingLabel ? pendingOnly : undefined,
        recvType: recvType || undefined,
        // 접수 필터는 노선 탭에만 뜻이 있다. 다른 탭으로 새어 나가면 조용히 0건이 된다.
        recvId: tabId === 'dispt' ? recvFilter?.id : undefined,
        // 한 접수로 좁혀 보는 중이면 미충족 필터를 끈다 — 그 접수의 노선을 **전부** 보러 온 것이다.
        unfilledOnly: tab.unfilledLabel && !recvFilter ? unfilledOnly : undefined,
        // 노선 탭 = 출발일 · 접수 탭 = 운행 기간이 겹치는 접수(#279).
        dateFrom: tabId !== 'bid' && dateFrom ? dateFrom : undefined,
        dateTo: tabId !== 'bid' && dateTo ? dateTo : undefined,
        sort: tabId === 'dispt' ? disptSort?.key : undefined,
        dir: tabId === 'dispt' ? disptSort?.dir : undefined,
        })
        // 어느 탭이 읽은 줄인지 붙인다 — 아래 `rows` 참고.
        .then((r) => ({ ...r, rows: r.rows.map((x) => ({ ...x, __tab: tab.id })) })),
    [tab, tabId, q, status, pendingOnly, unfilledOnly, page, PAGE, nonce, recvType, recvFilter, dateFrom, dateTo, disptSort, allInPeriod],
  );

  /**
   * 🔴 지금 탭이 읽은 줄만 쓴다(#278·#279 윤일향). 탭을 바꾸면 새 목록이 올 때까지 앞 탭의 줄이 남는데,
   *    노선 줄은 같은 접수 번호가 여러 개라 접수 목록(`key = 접수 번호`)에 그대로 그려지면 React 가
   *    겹친 키의 줄을 지우지 못하고 남겨 둔다 — 날짜·노선 수 없는 「유령 접수」가 목록에 계속 붙어 있었다.
   */
  const rows = useMemo(() => loadedRows.filter((r) => r.__tab === tab.id), [loadedRows, tab.id]);

  const selRecv = useMemo(
    () => rows.find((r) => Number(r.recv_id) === selRecvId) ?? null,
    [rows, selRecvId],
  );

  // 목록이 새로 오면 고른 접수를 다시 맞춘다. 아무것도 안 고른 상태로 두면 오른쪽이
  // 늘 비어 있어 "한눈에 보기"가 성립하지 않는다 — 첫 줄을 자동으로 고른다.
  useEffect(() => {
    if (tabId !== 'recv' || rows.length === 0) return;
    if (rows.some((r) => Number(r.recv_id) === selRecvId)) return;
    setSelRecvId(Number(rows[0]!.recv_id));
  }, [tabId, rows, selRecvId]);

  function switchTab(id: string) {
    if (id === tabId) return;
    const open = pageTabs.find((t) => t.key === dispatchKey(id));
    navigate(open ? `${open.loc.pathname}${open.loc.search}` : `${DISPATCH_PATH}?tab=${id}`);
  }

  /** 접수 → 노선 탭으로. 인라인은 200건에서 잘리므로 전체는 노선 화면 탭에서 페이지를 넘겨 본다. */
  function seeAllRoutes(recvId: number, label: string) {
    navigate(`${DISPATCH_PATH}?tab=dispt&recv=${recvId}&rl=${encodeURIComponent(label)}`);
  }
  // 노선 화면 탭이 `?recv=` 로 불려 오면(위 seeAllRoutes) 그 접수의 노선을 **전부** — 이번 달 기본 기간이 걸리면 다른 달 노선이 빠진다.
  useEffect(() => {
    if (tabId !== 'dispt') return;
    const p = new URLSearchParams(loc.search);
    const rid = Number(p.get('recv'));
    if (!Number.isInteger(rid) || rid <= 0) return;
    setRecvFilter({ id: rid, label: p.get('rl') ?? '' });
    setDateFrom('');
    setDateTo('');
    setPage(0);
    setDetail(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.search]);
  function clearRecvFilter() {
    setRecvFilter(null);
    const [a, b] = disptRange ?? monthRange(0);
    setDateFrom(a);
    setDateTo(b);
    setPage(0);
    navigate(`${DISPATCH_PATH}?tab=dispt`, { replace: true });
  }
  // 상단 메뉴로 「접수·배차」를 다시 열 때 마지막에 본 세부 탭부터(App.tsx Pages).
  const [, setLastTab] = useSticky<string>('dispatch.tab', tabId);

  /**
   * `Ctrl+C` / `Ctrl+V` 복사(2026-08-02 사용자 요청).
   *
   * · `Ctrl+C` — 오른쪽에서 **노선을 골랐으면 그 노선만**, 아니면 **고른 접수 통째로** 담는다.
   *   그래서 "전체 복사"와 "부분 복사"가 같은 키 하나로 갈린다.
   * · `Ctrl+V` — 복사 팝업을 **미리 채워서** 연다. 지금 고른 접수가 있으면 '이 접수에 더하기',
   *   없으면 '접수째 새로'. ⚠️ **자동 저장하지 않는다** — 노선·배차를 새로 만드는 일이고
   *   배차는 시간중복 가드에 걸려 건너뛸 수 있다. 무엇이 어디로 가는지 보고 누르게 한다.
   *
   * 🔴 입력칸 안에서, 또는 글자를 선택해 둔 상태에서는 **가로채지 않는다** — 그 `Ctrl+C` 는
   *    글자 복사다. 팝업이 떠 있을 때도 비켜선다(그 안에서 값을 복사하는 중일 수 있다).
   */
  // 화면 탭(#168)으로 숨어 있을 때도 비켜선다 — 다른 화면의 Ctrl+C 를 가로채면 안 된다.
  const pageActive = usePageActive();
  // 화면 탭 「↻」 — 오른쪽 접수 칸(자체 조회)도 같이 다시 읽게(목록은 useList 가 알아서).
  const refreshTick = usePageRefresh();
  const refreshNow = useRefreshRequest();
  useEffect(() => { if (pageActive) setLastTab(tabId); }, [pageActive, tabId, setLastTab]);
  const popupOpen = !pageActive || !!(copySrc || intake || editRecv !== null || disptForm || detail);
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      const k = e.key.toLowerCase();
      if (k !== 'c' && k !== 'v') return;
      if (popupOpen || isTypingTarget(e.target) || hasTextSelection()) return;

      if (k === 'c') {
        if (!selRecvId) return;
        const src = rows.find((r) => Number(r.recv_id) === selRecvId);
        const label = `${String(src?.cust_name ?? '')} 접수 #${selRecvId}`;
        const next = selDisptId
          ? writeClip({ kind: 'dispt', recvId: selRecvId, disptIds: [selDisptId], label: `${label} · 노선 1건` })
          : writeClip({ kind: 'recv', recvId: selRecvId, label });
        setClip(next);
        setNotice(`복사했습니다 — ${next.label}. 붙일 접수를 고르고 Ctrl+V`);
        e.preventDefault();
        return;
      }

      const c = readClip();
      if (!c) { setNotice('복사해 둔 것이 없습니다. 접수나 노선을 고르고 Ctrl+C 를 누르세요.'); return; }
      if (!canWrite) { setNotice('붙여넣기에는 배차 권한이 필요합니다.'); return; }
      const src = rows.find((r) => Number(r.recv_id) === c.recvId);
      // 원본이 지금 목록에 없어도 붙일 수 있어야 한다 — 서버는 id 로 읽는다.
      const row: Row = src ?? { recv_id: c.recvId, cust_name: c.label, recv_type: '' };
      const sameRecv = selRecvId !== null && selRecvId !== c.recvId;
      setCopySrc({
        row,
        preset: {
          disptIds: c.disptIds,
          dest: selRecvId === null ? 'new' : selRecvId === c.recvId ? 'same' : 'other',
          otherRecvId: sameRecv ? selRecvId : undefined,
        },
      });
      e.preventDefault();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [popupOpen, selRecvId, selDisptId, rows, canWrite]);

  function saved(msg: string) {
    setIntake(false);
    setEditRecv(null);
    setNotice(msg);
    setNonce((n) => n + 1);
  }

  // 접수 탭의 행 클릭은 **고르기**다(오른쪽 패널이 바뀐다). 수정·노선추가·복사 버튼은
  // 왼쪽 표에서 빼 오른쪽 요약 카드로 옮겼다 — 좁은 표에 버튼 3개를 더 넣을 자리가 없고,
  // 그 버튼들은 "고른 접수 하나"에 하는 일이라 카드 쪽이 제자리다.
  // (신규 접수는 접수와 노선을 한 화면에서 같이 넣는다 — RecvIntakeForm)
  const columns: Column[] = tab.columns ?? [];

  return (
    <div className="space-y-3">
      <div className="page-head">
        <h1 className="font-bold">배차</h1>
        <p className="mt-1 break-keep text-sm text-ink-soft">
          왼쪽에서 <b>접수</b>를 고르면 오른쪽에 <b>노선</b>이, 노선을 고르면 그 아래 <b>배차</b> 차량이 섭니다.
          {canWrite && ' 수정·노선 추가·복사는 오른쪽 카드에 있습니다.'}
          {' '}<b>노선</b> 탭은 접수를 건너뛰고 <b>대수가 안 찬 노선</b>만 모아 봅니다.
        </p>
        {/* 범위가 걸려 있으면 **그렇다고 말한다.** 조용히 좁히면 "자료가 없어졌다"는 신고가 되고,
            그때 원인이 필터라는 걸 아무도 못 찾는다. 전량을 보는 사람에게는 아무것도 안 뜬다. */}
        {me?.scopeNote && (
          <p className="mt-2 inline-block break-keep rounded-lg bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800">
            {me.scopeNote}
            {me.role !== 'admin' && ' 범위를 넓히려면 관리자에게 요청하세요.'}
          </p>
        )}
      </div>

      <div className="btn-row-sm flex flex-wrap items-center gap-3">
        <div className="flex gap-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => switchTab(t.id)}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
                t.id === tabId ? 'bg-brand-600 font-semibold text-white shadow-sm' : 'text-ink hover:bg-brand-50'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            setQ(term.trim());
            setPage(0);
          }}
          className="ml-auto flex flex-wrap items-center justify-end gap-2"
        >
          {tabId === 'recv' && canWrite && (
            <button type="button" className="btn-primary" onClick={() => setIntake(true)}>
              신규 접수
            </button>
          )}
          {tab.pendingLabel && (
            <label className="flex cursor-pointer select-none items-center gap-1.5 text-sm text-ink-soft">
              <input
                type="checkbox"
                checked={pendingOnly}
                onChange={(e) => {
                  setPendingOnly(e.target.checked);
                  setPage(0);
                }}
              />
              {tab.pendingLabel}
            </label>
          )}
          {/* 한 접수로 좁혀 보는 중에는 뜻이 없다 — 그때는 그 접수의 노선을 전부 보여준다. */}
          {tab.unfilledLabel && !recvFilter && (
            <label className="flex cursor-pointer select-none items-center gap-1.5 text-sm text-ink-soft">
              <input
                type="checkbox"
                checked={unfilledOnly}
                onChange={(e) => {
                  setUnfilledOnly(e.target.checked);
                  setPage(0);
                }}
              />
              {tab.unfilledLabel}
            </label>
          )}
          {/* #197 출발일 기간 — 기간을 고르면 그 기간 배차현황을 다 보러 온 것이라 「대수 미충족만」을 푼다. */}
          {tabId !== 'bid' && (
            <span className="flex flex-wrap items-center gap-1 text-sm text-ink-soft">
              {/* 접수 탭(#279 윤일향 「정산년월·일자 검색」) = 운행 기간이 이 날짜와 겹치는 접수. 월을 고르면 그 달 1일~말일. */}
              {tabId === 'recv' && (
                <input type="month" className="field w-36" aria-label="운행 월"
                       value={dateFrom && dateTo && dateFrom.slice(0, 7) === dateTo.slice(0, 7) && dateFrom.endsWith('-01') ? dateFrom.slice(0, 7) : ''}
                       onChange={(e) => {
                         const m = e.target.value;
                         if (!m) { setDateFrom(''); setDateTo(''); setPage(0); return; }
                         const [y, mo] = m.split('-').map(Number);
                         const last = new Date(y!, mo!, 0).getDate();
                         setDateFrom(`${m}-01`); setDateTo(`${m}-${String(last).padStart(2, '0')}`); setPage(0);
                       }} />
              )}
              {tabId === 'recv' ? '운행일' : '출발일'}
              <input type="date" className="field w-36" value={dateFrom} aria-label="시작일"
                     onChange={(e) => { setDateFrom(e.target.value); if (e.target.value) setUnfilledOnly(false); setPage(0); }} />
              ~
              <input type="date" className="field w-36" value={dateTo} aria-label="끝일"
                     onChange={(e) => { setDateTo(e.target.value); if (e.target.value) setUnfilledOnly(false); setPage(0); }} />
              {[0, 1].map((k) => (
                <button key={k} type="button" className="btn-ghost px-2 py-1 text-xs"
                        onClick={() => { const [a, b] = monthRange(k); setDateFrom(a); setDateTo(b); setUnfilledOnly(false); setPage(0); }}>
                  {k === 0 ? '이번 달' : '다음 달'}
                </button>
              ))}
              {(dateFrom || dateTo) && (
                <button type="button" className="text-xs underline" onClick={() => { setDateFrom(''); setDateTo(''); setPage(0); }}>
                  지우기
                </button>
              )}
            </span>
          )}
          {tabId !== 'bid' && (
            <select
              className="field w-28"
              value={recvType}
              onChange={(e) => {
                setRecvType(e.target.value);
                setPage(0);
              }}
            >
              <option value="">구분 전체</option>
              <option value="일반">일반</option>
              <option value="통근">통근</option>
            </select>
          )}
          {tab.statuses.length > 0 && (
            <select
              className="field w-36"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(0);
              }}
            >
              <option value="">상태 전체</option>
              {tab.statuses.map((s) => (
                <option key={s} value={s}>
                  {statusLabel(s)}
                </option>
              ))}
            </select>
          )}
          <input className="field w-64" placeholder={tab.hint} value={term} onChange={(e) => setTerm(e.target.value)} />
          <button className="btn-ghost" type="submit">
            검색
          </button>
          {/* #357 — 화면 탭 줄의 ↻ 와 같은 일(고른 조건 그대로 다시 읽기) · 눈에 띄게 목록 옆에도. */}
          <button className="btn-ghost" type="button" title="이 목록을 최신 내용으로 다시 읽습니다(고른 조건은 그대로)"
                  onClick={refreshNow}>
            ↻ 새로고침
          </button>
        </form>
      </div>

      {/* 기본으로 켜 둔 필터는 **왜 켜져 있는지까지** 적는다. 체크박스만 있으면
          "노선이 안 보인다"가 되고, 그 사람은 자기가 무엇을 안 보고 있는지 모른다. */}
      {tabId === 'dispt' && tab.unfilledLabel && !recvFilter && unfilledOnly && (
        <div className="-mt-1 text-xs text-ink-soft">
          <b className="text-ink">대수가 안 찬 노선만</b> 보는 중(배차완료 숨김) — 위 <b>{tab.unfilledLabel}</b> 체크를 풀면 전량.
        </div>
      )}

      {/* 좁혀 놓은 필터는 화면에 남겨 둔다 — 숨기면 "왜 이것만 보이지"가 된다. */}
      {tabId === 'dispt' && recvFilter && (
        <div className="flex items-center gap-2 text-sm">
          <span className="rounded bg-brand-50 px-2 py-1 font-medium text-brand-700">
            접수 #{recvFilter.id} · {recvFilter.label} 의 노선만
          </span>
          <button className="text-xs underline" onClick={clearRecvFilter}>
            필터 해제
          </button>
        </div>
      )}

      {error && <ErrorBox message={error} />}

      {/*
        🔴 **무엇을 들고 있는지 보이게 둔다.** 클립보드가 안 보이면 며칠 전에 담은 접수를
        엉뚱한 곳에 붙이게 되고, 붙인 뒤에는 되돌리기가 어렵다(노선·배차가 새로 생긴다).
        담은 시각까지 적어 "오래된 것"을 눈으로 알 수 있게 한다.
      */}
      {clip && (
        <div
          className="card flex flex-wrap items-center gap-2 border-brand-200 bg-brand-50 px-2 py-1 text-xs"
          title="붙일 접수를 고르고 Ctrl+V — 무엇을 어디에 붙일지 팝업에서 다시 고를 수 있습니다."
        >
          <span className="rounded bg-white px-1.5 py-0.5 font-medium text-brand-700">
            복사됨
          </span>
          <b>{clip.label}</b>
          <span className="text-ink-faint">
            {clip.at.slice(0, 16).replace('T', ' ')} · {clip.kind === 'dispt' ? '고른 노선만' : '접수 전체'}
          </span>
          <span className="text-ink-soft">
            붙일 접수를 고르고 <b>Ctrl+V</b>
          </span>
          <button
            className="ml-auto text-xs underline"
            onClick={() => { clearClip(); setClip(null); }}
          >
            비우기
          </button>
        </div>
      )}


      {/* 입력 폼은 전부 **팝업**으로 띄운다. 페이지 위쪽에 인라인으로 열면 아래로 스크롤한
          상태에서는 눌러도 아무 일도 안 일어난 것처럼 보인다(2026-07-31). 여백 클릭으로
          닫히지 않는다 — 그 기본값은 `Modal` 이 들고 있다. */}
      {copySrc && (
        <Modal>
          <RecvCopy
            recv={{
              recv_id: Number(copySrc.row.recv_id),
              cust_name: copySrc.row.cust_name,
              recv_type: copySrc.row.recv_type,
            }}
            preset={copySrc.preset}
            onClose={() => setCopySrc(null)}
            onCopied={(m) => {
              setCopySrc(null);
              saved(m);
            }}
          />
        </Modal>
      )}

      {intake && (
        // 접수 헤더 + 노선 여러 줄이 들어가는 표라 넓게 띄운다.
        <Modal width="max-w-[min(120rem,96vw)]">
          <RecvIntakeForm onClose={() => setIntake(false)} onSaved={saved} />
        </Modal>
      )}

      {editRecv !== null && (
        <Modal>
          <RecvForm recvId={editRecv} onClose={() => setEditRecv(null)} onSaved={saved} />
        </Modal>
      )}

      {disptForm && (
        <Modal width="max-w-4xl">
          <DisptForm
            recvId={disptForm.recvId}
            disptId={disptForm.disptId}
            copyFromId={disptForm.copyFromId}
            onClose={() => setDisptForm(null)}
            onSaved={(m) => {
              setDisptForm(null);
              saved(m);
              setDetail(null);
            }}
          />
        </Modal>
      )}

      {/* 노선 상세는 팝업(10-15 way 「위가 넓어 아래 목록이 한눈에 안 보인다」) — 표 위에 카드로 끼우면 상세를 여는 순간
          목록이 화면 밖으로 밀렸다(창 828 → 페이지 1031). 팝업이면 목록 높이가 그대로다. 닫기 = 카드의 「닫기」. */}
      {/* 「노선 수정·복사」 창이 뜨면 잠시 숨긴다(같은 층 팝업이라 뒤에 그린 상세가 수정 창을 덮는다). */}
      {detail && !disptForm && editRecv === null && (
        <Modal width="max-w-[min(110rem,96vw)]">
        <DisptDetail
          dispt={detail}
          onClose={() => setDetail(null)}
          onChanged={(m) => {
            setNotice(m);
            setNonce((n) => n + 1);
          }}
          onEdit={() =>
            setDisptForm({ recvId: Number(detail.recv_id), disptId: Number(detail.dispt_id) })
          }
          onCopy={() => setDisptForm({ recvId: Number(detail.recv_id), copyFromId: Number(detail.dispt_id) })}
          onAdd={() => setDisptForm({ recvId: Number(detail.recv_id) })}
          onEditRecv={() => setEditRecv(Number(detail.recv_id))}
          onDeleted={(m) => {
            setDetail(null);
            setNotice(m);
            setNonce((n) => n + 1);
          }}
        />
        </Modal>
      )}

      {tabId === 'recv' ? (
        // 좌우 2단. 왼쪽에서 접수를 옮겨 다녀도 표가 밀리지 않는다 —
        // 줄 아래에 펼치는 방식은 아래 행을 밀어내서 접수를 비교할 수 없었다.
        // xl 미만(노트북)에서는 위아래로 접힌다: 반쪽 폭에 노선 표가 들어가지 않는다.
        <div className="grid items-start gap-4 xl:grid-cols-[22rem_minmax(0,1fr)]">
          {/* 왼쪽 칸은 xl 에서 메뉴 밑에 붙어 있는다(10-15 way 캡처) — 노선을 고르면 배차 칸을 보이게 하려고 페이지가 내려가는데,
              붙어 있지 않으면 접수 목록이 같이 위로 밀려 고를 때마다 목록이 떨어지는 것처럼 보였다. */}
          <div className="space-y-3 xl:sticky xl:top-[calc(var(--top-bar,0px)+0.5rem)]">
            {/* 표가 아니라 목록이다 — 좁은 칸에서 표는 긴 거래처명에 끌려가 뒤쪽 칸을 잘라먹는다. */}
            <RecvList
              rows={rows}
              loading={loading}
              selectedId={selRecvId}
              // 조회 권한만 있어도 고를 수 있다 — 노선을 보는 건 읽기다.
              onPick={setSelRecvId}
            />
            <Pager page={page} setPage={setPage} rowCount={rows.length} pageSize={PAGE} loading={loading} setPageSize={setPAGE} baseSize={50} />
          </div>

          {selRecv ? (
            <RecvPane
              recv={selRecv}
              canWrite={canWrite}
              nonce={nonce + refreshTick}
              onEditRecv={setEditRecv}
              onAddRoute={(id) => setDisptForm({ recvId: id })}
              onCopy={(r) => setCopySrc({ row: r })}
              onPickRoute={setSelDisptId}
              onSeeAllRoutes={seeAllRoutes}
              onEditRoute={(rid, did) => setDisptForm({ recvId: rid, disptId: did })}
              onCopyRoute={(rid, did) => setDisptForm({ recvId: rid, copyFromId: did })}
              onChanged={(m) => {
                setNotice(m);
                setNonce((n) => n + 1);
              }}
            />
          ) : (
            <div className="card p-6 text-sm text-ink-faint">
              {loading ? '불러오는 중…' : '왼쪽에서 접수를 고르면 그 접수의 노선과 배차가 여기 나옵니다.'}
            </div>
          )}
        </div>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={rows}
            loading={loading}
            onRowClick={
              tab.clickable ? (r) => { setPicked(Number(r.dispt_id)); setDetail(r); } : undefined
            }
            onRowSelect={tabId === 'dispt' ? (r) => setPicked(Number(r.dispt_id)) : undefined}
            activeRow={tab.clickable ? (r) => Number(r.dispt_id) === (detail ? Number(detail.dispt_id) : picked) : undefined}
            total={total}
            // 탭마다 칼럼이 다르다(노선·입찰). 키에 탭을 넣어 서로를 덮지 않게 한다.
            tableId={`dispatch.${tabId}`}
            sort={tabId === 'dispt' ? disptSort : undefined}
            onSort={tabId === 'dispt' ? (k) => { toggleDisptSort(k); setPage(0); } : undefined}
          />
          {allInPeriod ? (
            <p className="text-xs text-ink-faint">
              {loading ? '불러오는 중…' : `출발일 기간 안 ${rows.length.toLocaleString()}건을 한 쪽에 모두 보이고 있습니다.`}
              {!loading && rows.length >= PERIOD_ALL && ' 한 번에 받을 수 있는 수를 넘었습니다 — 기간을 줄여 주세요.'}
            </p>
          ) : (
            <Pager page={page} setPage={setPage} rowCount={rows.length} pageSize={PAGE} loading={loading} setPageSize={setPAGE} baseSize={50} />
          )}
        </>
      )}
    </div>
  );
}
